import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { TRootState } from 'store/types'
import { voiceCatalogSelectors } from 'store/features/voiceCatalog/selectors'
import { userSelectors } from 'store/features/user/selectors'
import { useOpenUpgradePopup } from 'hooks/useOpenUpgradePopup'
import { REF_LINKS } from 'consts/refLinks'
import { ITtsSpeaker } from 'types/text-to-speech'
import { checkIsPremiumVoice, getSpeakersCacheKey } from 'utils/tts'

export const useGetTtsSpeakers = (langCode: string, model?: string) => {
  const cacheKey = getSpeakersCacheKey(langCode, model)

  const ttsSpeakers = useSelector((state: TRootState) => {
    const speakersPerLangCode =
      voiceCatalogSelectors.speakersPerLangCodeSelector(state, cacheKey)
    return speakersPerLangCode || null
  })

  const isSpeakersLoading = useSelector((state: TRootState) =>
    voiceCatalogSelectors.speakersPerLangCodeIsLoadingSelector(state, cacheKey)
  )

  return { ttsSpeakers, isSpeakersLoading }
}

export const useGetTtsItemSpeakerData = (
  speakerId: number,
  langCode: string,
  model?: string
) => {
  const cacheKey = getSpeakersCacheKey(langCode, model)
  const ttsSpeakers = useSelector((state: TRootState) =>
    voiceCatalogSelectors.speakersPerLangCodeSelector(state, cacheKey)
  )
  return ttsSpeakers?.find(speaker => speaker.id === speakerId)
}

export const useGetCurrentSpeakerHasEmotion = (
  speakerId: number,
  langCode: string,
  model?: string
) => {
  const speakerData = useGetTtsItemSpeakerData(speakerId, langCode, model)

  return speakerData?.hasEmotion || false
}

export const usePremiumVoiceHandler = ({
  speakers,
  onVoiceSelect
}: {
  speakers: ITtsSpeaker[]
  onVoiceSelect?: (speakerId: number) => void
}) => {
  const isFreeUser = useSelector(userSelectors.isFreeUser)
  const ttsLimitButtonAction = useOpenUpgradePopup()

  const handleVoiceSelection = useCallback(
    (speakerId: number) => {
      const isPremiumVoice = checkIsPremiumVoice(speakerId, speakers)
      const isUpgradePopupNeeded = isFreeUser && isPremiumVoice

      if (isUpgradePopupNeeded) {
        ttsLimitButtonAction({ ref: REF_LINKS.V_EDITOR_TTS_GENERATE })
      } else {
        onVoiceSelect?.(speakerId)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFreeUser, speakers]
  )

  return {
    handleVoiceSelection
  }
}
